import { SiteVariablesPrepared } from "@fluentui/styles";
import { ValidatorDictionary, validateRequired, validators } from "@shopify/react-form-state";
import { StripeCardElementOptions } from "@stripe/stripe-js";
import {
  PaymentFrequency,
  StandardPlanLevels,
  TeamsSubscriptionPlanUsageTypes,
  TeamsSubscriptionPlans,
} from "@teams/types";
import { CouponDTO } from "@teams/types/api";

import { isValidEmail } from "../validators";

export const getPlanType = (planId?: string): "Pro" | "Standard" | "Enterprise" => {
  if (
    planId?.startsWith("teams-standard-2500") ||
    planId?.startsWith("teams-standard-5000") ||
    planId?.startsWith("teams-pro-unlimited")
  ) {
    return "Pro";
  } else if (planId?.startsWith("teams-standard-500")) {
    return "Standard";
  } else if (planId === "enterprise") {
    return "Enterprise";
  } else {
    return "Standard";
  }
};

export type PlanFrequencyOption = { frequency: PaymentFrequency; price: number };

export type CheckoutPlan = {
  planLevels?: StandardPlanLevels[];
  planType: TeamsSubscriptionPlanUsageTypes;
  quantity: number;
  frequency: PaymentFrequency;
  frequencyOptions: (planId: string) => PlanFrequencyOption[];
};

export type PaymentInput = {
  name: string;
  email: string;
  coupon: CouponDTO | undefined;
  frequency: PaymentFrequency | undefined;
  licenses: number;
  planId: string;
};

export const teamsFeatures: [string, boolean][] = [
  ["Multi-Question Pollys", true],
  ["Recurring Pollys", true],
  ["Custom Templates", true],
  ["Polly Reminders", true],
  ["Exports", true],
];

export const upgradeFeatures: [string, boolean][] = [["All features from free plan", true]];

export const getPlanFrequencyOptions = (
  plan: TeamsSubscriptionPlans,
  planLevels: StandardPlanLevels[]
): PlanFrequencyOption[] => {
  switch (plan) {
    case "agile-standup":
      return getAgileStandupPlanFrequencies();
    case "msteams-standard":
      return getMSTeamsPlanLevelFrequencies(planLevels)(plan);
    default:
      throw new Error("Unknown plan type");
  }
};

export const getAgileStandupPlanFrequencies = (): PlanFrequencyOption[] => {
  return [
    {
      frequency: "monthly",
      price: 3,
    },
  ];
};

export const getAgileStandupStandardPlan = (): CheckoutPlan => {
  return {
    planType: "metered",
    quantity: 5,
    frequency: "monthly",
    frequencyOptions: () => getAgileStandupPlanFrequencies(),
  };
};

export const getMSTeamsPlanLevelFrequencies =
  (planLevels: StandardPlanLevels[]) =>
  (planId: string): PlanFrequencyOption[] => {
    let level = planLevels.find(p => p.id === planId);
    if (!level) {
      level = planLevels[0];
    }

    return [
      { frequency: "yearly", price: level.yearlyPrice },
      { frequency: "quarterly", price: level.quarterlyPrice },
      { frequency: "semi_annually", price: level.semi_annuallyPrice },
      { frequency: "monthly", price: level.monthlyPrice },
    ];
  };

export const getPlanLevelDisplayText = (planLevel: number | undefined) => {
  return planLevel === Number.MAX_SAFE_INTEGER ? "Unlimited" : planLevel;
};

export const getStripeCardOptions = (
  siteVariables: SiteVariablesPrepared
): StripeCardElementOptions => {
  //NOTE: Any changes you make here need to be tested against theme switching.  Stripe does not react well to font size changes in particular
  return {
    style: {
      base: {
        color: siteVariables.bodyColor,
        fontFamily: siteVariables.bodyFontFamily,
        "::placeholder": {
          color: "#95AEBF",
        },
      },
    },
  };
};

export function getPaymentValidator(): Partial<ValidatorDictionary<PaymentInput>> {
  return {
    name: validators.required("Name is required"),
    email: [validateRequired(isValidEmail, "A valid email is required")],
  };
}
